'use client'

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { FieldNumericInput, FieldNumericWrapper } from './FieldNumeric.styles'

interface InputProps {
  digits?: number
  placeholder?: string
  defaultValue?: string | null
  onChange: (otp: string) => void
  disabled?: boolean
  required?: boolean
  error?: string | null
  className?: string
  readonly?: boolean
}

const FieldNumeric = forwardRef<HTMLDivElement, InputProps>(
  ({ digits = 10, placeholder, defaultValue, onChange, disabled, required, className, readonly }, ref) => {
    const [otp, setOtp] = useState(Array(digits).fill(''))
    const inputsRef = useRef<(HTMLInputElement | null)[]>([])
    const [focused, setFocused] = useState(false)

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const paste = e.clipboardData.getData('text')

      if (new RegExp(`^\\d{${digits}}$`).test(paste)) {
        const newOtp = paste.split('')
        setOtp(newOtp)
        onChange(newOtp.join(''))
        inputsRef.current.forEach((input, index) => {
          if (input) input.value = newOtp[index]
        })
      }
    }

    const focusInput = (index: number) => {
      inputsRef.current[index]?.focus()
    }

    // @ts-ignore
    useImperativeHandle(ref, () => ({
      focus: () => focusInput(0)
    }))

    useEffect(() => {
      inputsRef.current.forEach(input => {
        if (input) {
          input.addEventListener('paste', handlePaste as unknown as EventListener)
          input.addEventListener('focus', () => setFocused(true))
          input.addEventListener('blur', () => setFocused(false))
        }
      })
      return () => {
        inputsRef.current.forEach(input => {
          if (input) {
            input.removeEventListener('paste', handlePaste as unknown as EventListener)
            input.removeEventListener('focus', () => setFocused(true))
            input.removeEventListener('blur', () => setFocused(false))
          }
        })
      }
    }, [])

    useEffect(() => {
      if (defaultValue) {
        const formattedOtp = defaultValue.slice(0, digits).split('')
        setOtp([...formattedOtp, ...Array(digits - formattedOtp.length).fill('')])
      } else {
        setOtp(Array(digits).fill(''))
      }
    }, [defaultValue])

    const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (!/^[0-9]?$/.test(value)) return
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)
      onChange(newOtp.join(''))
      if (value && index < digits - 1) inputsRef.current[index + 1]?.focus()
    }

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputsRef.current[index - 1]?.focus()
      }
    }

    return (
      <FieldNumericWrapper ref={ref} className={className} focused={focused}>
        {otp.map((digit, index) => (
          <FieldNumericInput
            key={index}
            ref={el => {
              inputsRef.current[index] = el
            }}
            disabled={disabled}
            maxLength={1}
            placeholder={placeholder}
            required={required}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            value={digit}
            onChange={e => handleChange(index, e)}
            onKeyDown={e => handleKeyDown(index, e)}
            readOnly={readonly}
          />
        ))}
      </FieldNumericWrapper>
    )
  }
)

FieldNumeric.displayName = 'FieldNumeric'

export default FieldNumeric
